/**
 * La barra de arriba: el CAMINO de cinco etapas (Fase 12). Y, de paso, la lista de secciones de la izquierda.
 *
 * REDISEÑO DEL 11-sep-2026 («HumanInk Editor Studio»). Arriba ya no hay fases que navegar: hay
 * cuatro verbos —Analizar · Reescribir · Corregir · Publicar— y cada uno abre el chat con el
 * agente que toca y un encargo mínimo ya escrito, que el autor completa o manda tal cual. Es lo
 * mismo que elegir al agente y ponerse a hablar, pero con el primer paso dado: una especie de
 * onboarding por acción. Las SECCIONES (Modelos · Autores · Estilos · Proyectos · Manuscrito ·
 * Auditoría) viven en la barra izquierda; se definen aquí, con las acciones, para que la guardia
 * «ninguna sección promete en silencio» tenga una sola fuente que leer.
 */
import { useEffect, useRef } from "react";
import type { Etapa } from "../logica";

export interface Item { id: string; et: string; ico: string }

/** Bloque 1 de la izquierda: el estudio. Orden del documento de Rais. */
export const SECCIONES: Item[] = [
  { id: "modelos", et: "Modelos", ico: "⚙" },
  { id: "autores", et: "Autores", ico: "◉" },
  { id: "estilos", et: "Estilos", ico: "✎" },
  { id: "proyectos", et: "Proyectos", ico: "▤" },
  { id: "manuscrito", et: "Manuscrito", ico: "▥" },
  { id: "auditoria", et: "Auditoría", ico: "▣" },
];

/** Pantallas que cuelgan de una sección: para encender la entrada correcta a la izquierda. */
export const PADRE: Record<string, string> = {
  panel: "proyectos", bitacora: "proyectos", versiones: "proyectos", comparar: "proyectos", "informe-lectura": "proyectos",
  gasto: "proyectos",
  biblia: "manuscrito", escaleta: "manuscrito",
  certificado: "auditoria", procedencia: "auditoria", registro: "auditoria",
};

/** Lo que cuelga de cada sección. Auditoría lo enseña como fichas; la barra izquierda, indentado
 *  bajo la sección encendida. Proyectos (R-01, 17-sep): Informes, Pasadas y Comparar existían y
 *  funcionaban, pero desde la barra rediseñada no se llegaba a ellas. */
export const HIJAS: Record<string, Item[]> = {
  proyectos: [
    { id: "bitacora", et: "Bitácora", ico: "≡" },
    { id: "informe-lectura", et: "Informes", ico: "◧" },
    // «Versiones», no «Pasadas»: compartía el id con la etapa de arriba y la pantalla montaba la
    // curva dos veces (revisión del 17-sep). «Comparar» era la misma pantalla con otro nombre.
    { id: "versiones", et: "Versiones", ico: "↗" },
    // 23-sep: el total de abajo dice cuánto; esto dice en qué.
    { id: "gasto", et: "Gasto", ico: "$" },
  ],
  auditoria: [
    { id: "certificado", et: "Certificado", ico: "▣" },
    { id: "procedencia", et: "Procedencia", ico: "⎋" },
    { id: "registro", et: "Registro", ico: "◉" },
  ],
};

/** Bloque 2 de la izquierda: los cuatro agentes editoriales, por su id en el motor. */
// `nombre`, no `et`: la guardia de la QA lee `{ id, et }` como «sección de la barra», y los
// agentes no son secciones.
export const AGENTES: { id: string; nombre: string; oficio: string }[] = [
  { id: "coach", nombre: "Coach literario", oficio: "informes, biblia, escaleta, mercado" },
  { id: "ghostwriter", nombre: "Escritor fantasma", oficio: "escribe con tu estilo y tus documentos" },
  { id: "editor", nombre: "Editor de desarrollo", oficio: "el plan y cada paso de la reescritura" },
  { id: "copyeditor", nombre: "Corrector", oficio: "línea, copia, ortotipografía, humanizador" },
  // 17-sep (Rais): «en Publicar no sale ninguno de los colaboradores». La acción abría un chat con
  // un agente que no aparecía en la barra. Un motor anterior a Agentes I no lo trae y no sale.
  { id: "publicar", nombre: "Publicación", oficio: "maquetación, ficha de Amazon, portada, lanzamiento" },
];

/** Con quién se habla en cada etapa y el encargo mínimo que se deja escrito al abrir el chat. */
export const HABLAR_EN_ETAPA: Record<string, { agente: string; encargo: string }> = {
  diagnostico: { agente: "coach", encargo: "Quiero entender el diagnóstico de mi manuscrito. ¿Qué es lo más importante que tengo que saber?" },
  plan: { agente: "editor", encargo: "Mira los informes de mi manuscrito y prepara el plan de reescritura: fases, pasadas y qué no se toca." },
  pasadas: { agente: "editor", encargo: "Vamos con la siguiente pasada del plan. Dime cuál es y qué vas a cambiar antes de hacerlo." },
  correccion: { agente: "copyeditor", encargo: "Quiero corregir el manuscrito: línea, estilo y ortotipografía, y quitar el IA-slop con el humanizador. Dime por dónde empezamos." },
  publicacion: { agente: "publicar", encargo: "Quiero preparar la publicación: maquetación, ficha de Amazon, portada y el plan de lanzamiento. Dime por dónde empezamos." },
};

// ── EL STUDIO COMO FLUJO (3-oct-2026; ver src/flujo.ts) ──────────────────────────────────────────
// Orden de Rais: Modelos · Autores · Proyectos · Manuscrito · Control · Estilo. Sin Auditoría (AWAP
// será otro producto) y, con ella, sin biblia ni escaleta. «Control» es el cuadro de mandos: las
// cifras del texto y cómo cambian de una versión a otra.
export const SECCIONES_FLUJO: Item[] = [
  { id: "modelos", et: "Modelos", ico: "⚙" },
  { id: "autores", et: "Autores", ico: "◉" },
  { id: "proyectos", et: "Proyectos", ico: "▤" },
  { id: "manuscrito", et: "Manuscrito", ico: "▥" },
  { id: "control", et: "Control", ico: "◔" },
  { id: "estilos", et: "Estilo", ico: "✎" },
];

/** Las pantallas que ya no tienen entrada propia y a qué sección encienden. */
export const PADRE_FLUJO: Record<string, string> = {
  panel: "proyectos", versiones: "control", comparar: "control", gasto: "control", bitacora: "control",
};

/** El único con quien se habla. El motor lo llama `coach`, y ya reúne varios oficios. */
export const COACH = { id: "coach", nombre: "Coach literario", oficio: "pregúntale lo que quieras de tu libro" };

/** Lo que se le deja escrito al coach al abrirlo desde cada fase. */
export const PREGUNTA_AL_COACH: Record<string, string> = {
  diagnostico: "Quiero entender el diagnóstico de mi manuscrito. ¿Qué es lo más importante que tengo que saber?",
  plan: "Explícame el plan de reescritura: qué se va a hacer en cada fase y por qué en ese orden.",
  pasadas: "Tengo dudas con los cambios de esta fase. Ayúdame a decidir qué acepto y qué no.",
  correccion: "¿Qué va a tocar la corrección y qué no? Dime qué tengo que mirar después en Word.",
  publicacion: "¿Me conviene buscar editorial o publicar por mi cuenta? Dime qué necesito para cada camino.",
};

export const TODAS: Item[] = [...SECCIONES, ...Object.values(HIJAS).flat()];

/**
 * LA BARRA DE ARRIBA ES EL CAMINO (Fase 12, R-09): Diagnóstico · Plan · Pasadas · Corrección ·
 * Publicación, cada una con su estado. Sustituye a las cuatro acciones (Analizar · Reescribir ·
 * Corregir · Publicar), que abrían un chat que no podía ejecutarlas. Las etapas recomiendan, no
 * bloquean: todas se pueden abrir, y la que conviene hacer antes lo dice.
 */
export default function Barra(
  { etapas, activa, alEtapa, trabajando = null }: { etapas: Etapa[]; activa: string; alEtapa: (id: string) => void;
    /** La etapa en la que el motor está trabajando ahora mismo: se ve sin entrar en ella. */
    trabajando?: string | null },
) {
  const tira = useRef<HTMLElement>(null);
  useEffect(() => {
    tira.current?.querySelector(".etapa.on")?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activa]);

  return (
    <nav className="camino" ref={tira} aria-label="El camino de tu libro">
      {etapas.map((e, i) => (
        <span key={e.id} className="paso-camino">
          {i > 0 && <span className="flecha" aria-hidden>›</span>}
          <button className={`etapa ${e.estado}${activa === e.id ? " on" : ""}${trabajando === e.id ? " trabajando" : ""}`} onClick={() => alEtapa(e.id)}
                  title={e.recomendado ? `Recomendado antes: ${e.recomendado}` : undefined}>
            <span className="n"><span className="pt" />{e.nombre}</span>
            <span className="e">{trabajando === e.id ? "trabajando…" : e.recomendado && e.estado === "sin_empezar" ? e.recomendado : e.detalle}</span>
          </button>
        </span>
      ))}
    </nav>
  );
}
